import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { toast } from "@/hooks/errors/use-toast";

export function useSignin() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const signIn = async (email: string, password: string) => {

    try {
      setIsSubmitting(true);
      
      // Attempt to sign in the user (BotID protection handled by API)
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        console.error("Sign in error details:", error);
        
        // Handle specific error conditions
        if (error.message?.toLowerCase().includes("invalid login credentials") ||
            error.message?.toLowerCase().includes("invalid email or password") ||
            error.message?.toLowerCase().includes("email not confirmed")) {
          toast({
            title: "Sign In Failed",
            description: "Invalid email or password. Please check your credentials and try again.",
            variant: "destructive",
          });
          return false;
        }
        
        if (error.message?.toLowerCase().includes("email not confirmed")) {
          toast({
            title: "Email Not Verified",
            description: "Please check your email and click the verification link before signing in.",
            variant: "warning",
          });
          return false;
        }
        
        // Handle server errors (500)
        if (error.status === 500) {
          toast({
            title: "Sign In Error",
            description: "There was a problem with the sign in service. Please try again later.",
            variant: "destructive",
          });
          return false;
        }
        
        throw error;
      }

      // Check if user data exists
      if (!data.user) {
        toast({
          title: "Sign In Error",
          description: "Unable to sign in. Please try again.",
          variant: "destructive",
        });
        return false;
      }

      // Success - navigate to home
      toast({
        title: "Welcome back!",
        description: "You have successfully signed in.",
        variant: "default",
      });
      
      navigate('/home');
      return true;

    } catch (error: any) {
      console.error("Sign in error:", error);
      
      // Improved error handling with user-friendly messages
      let errorMessage = "An unexpected error occurred during sign in";
      
      if (error.message) {
        if (error.message.toLowerCase().includes("invalid login credentials") ||
            error.message.toLowerCase().includes("invalid email or password")) {
          errorMessage = "Invalid email or password. Please check your credentials and try again.";
        } else if (error.message.toLowerCase().includes("email not confirmed")) {
          errorMessage = "Please verify your email address before signing in.";
        } else if (error.message.toLowerCase().includes("too many requests")) {
          errorMessage = "Too many sign in attempts. Please wait a moment and try again.";
        } else {
          errorMessage = error.message;
        }
      }
      
      // Handle server errors
      if (error.status === 500) {
        errorMessage = "There was a problem with the sign in service. Please try again later.";
      }
      
      toast({
        title: "Sign In Error",
        description: errorMessage,
        variant: "destructive",
      });
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    signIn,
    isSubmitting,
    loading: isSubmitting // Alias for compatibility
  };
} 